import Component from '@glimmer/component'
import { action } from '@ember/object'
import { isPresent } from '@ember/utils'
import { service } from '@ember/service'
import { task, timeout } from 'ember-concurrency'
import {
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortingConfig,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import { Interval } from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import { type IntlService, t } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import { tracked } from '@glimmer/tracking'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import type { PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import type { SafeString } from '@ember/template'
import type { DurationConfig } from 'teacher-ui-client/components/stable-table/basic-table'
import type { RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import DurationUnitCell from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import ClickableCell from 'teacher-ui-client/components/stable-table/basic-cells/clickable'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import applyTransform from '@blakeelearning/data-tables/modifiers/apply-transform'
import { hash } from '@ember/helper'
import { and, not } from 'ember-truth-helpers'
import type RouterService from '@ember/routing/router-service'
import type { MentalMinuteOverviewAggregate } from 'teacher-ui-client/utils/student-events/fetchers/mental-minute-overview'

interface Args {
  students?: StudentResultsOverviewStudentItem[]
  aggregate?: MentalMinuteOverviewAggregate
  studentRouteName: string
  onSortClick?: ({ sortKey }: SortObject) => void
  sortingConfig?: SortingConfig
  paginationConfig?: PaginationConfig
  exportable?: boolean
}

interface Signature {
  Args: Args
}

export interface StudentResultsOverviewStudentItem extends RowObject {
  student_id: number
  first_name: string
  last_name: string
  external_id: Nullable<string>
  correct_answers: number
  sprints_attempted: number
  sprints_mastered: number
  time_on_task: number
  highest_skill_mastered: number
  highest_lesson_passed: {
    id: number
    name: string
    label: string
  }
  scores: {
    average_first_score: number
    average_best_score: number
    average_improvement: number
  }
}

interface Column {
  value?: string | SafeString
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SortableCell
  cellComponent?: typeof DurationUnitCell
  lessonId?: string
  durationConfig?: DurationConfig
}

export class FluencySprintsStudentResultsOverview extends Component<Signature> {
  @service router!: RouterService
  @service intl!: IntlService
  @service dataTransformer!: DataTransformerService

  @tracked transformedItems: StudentResultsOverviewStudentItem[] = []
  @tracked unpaginatedItems: StudentResultsOverviewStudentItem[] = []
  @tracked pageCount: number = 1
  /**
   * Returns an array of operation descriptions that will be applied to the items
   */

  get pipelineDescriptors() {
    return [this.sortingDescriptor, this.paginationDescriptor]
  }

  get sortingDescriptor() {
    const tableColumns = this.columns
    if (!this.args.sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(tableColumns)
    const sortingConfigWithComparerKeys = injectComparerKeys(this.args.sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort', config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  get paginationDescriptor() {
    if (!this.args.paginationConfig) return null
    return { type: 'paginate', config: this.args.paginationConfig }
  }

  /**
   * This task provides an array of items and operations to the data transformer service, which will asynchronously
   * transform the data according to the operations, then set those transformed items for display in the table
   */
  @action
  performTransformItems(_element: unknown, [items, pipelineDescriptors]: [Args['students'], PipelineDescriptor[]]) {
    void this.transformItems.perform(items, pipelineDescriptors)
  }

  transformItems = task(
    { restartable: true },
    async (items: Args['students'] = [], pipelineDescriptors: PipelineDescriptor[] = []) => {
      await timeout(200) // debounce

      const presentDescriptors = pipelineDescriptors.filter(isPresent)
      const transformedData = await this.dataTransformer.buildAndTransform<
        StudentResultsOverviewStudentItem,
        StudentResultsOverviewStudentItem
      >(items, presentDescriptors)

      this.pageCount = transformedData.pageCount
      this.transformedItems = transformedData.items
      this.unpaginatedItems = transformedData.paginated?.flat() ?? []
    },
  )

  /**
   * Defines how the durations should be formatted
   */
  get durationConfig(): DurationConfig {
    const splitDurationOptions = {
      max: Interval.Days,
      min: Interval.Minutes,
      maxIntervals: 2,
    }
    const dataInterval = 'seconds'
    return { dataInterval, splitDurationOptions }
  }

  get defaultColumn(): Column {
    return {
      classNames: 'table-box table-box-header bg-ms-green-100',
      cellClassNames: 'table-box table-box-cell bg-ms-green-50 group-hover:bg-ms-green-100',
      component: SortableCell,
      comparerKey: 'sortByNumber',
    }
  }

  get columns(): Column[] {
    const { durationConfig, intl, defaultColumn } = this
    const translationPrefix = 'tables.fluencySprints.studentResultsOverview.headers'

    return [
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.firstName`),
        valuePath: 'first_name',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.lastName`),
        valuePath: 'last_name',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.timeOnTask`),
        valuePath: 'time_on_task',
        cellComponent: DurationUnitCell,
        durationConfig,
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.highestSkillMastered`),
        sortKey: 'highest_lesson_passed.id',
        valuePath: 'highest_lesson_passed.label',
      },
      { ...defaultColumn, value: intl.t(`${translationPrefix}.sprintsMastered`), valuePath: 'sprints_mastered' },
      { ...defaultColumn, value: intl.t(`${translationPrefix}.questionsAnswered`), valuePath: 'correct_answers' },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.firstScore`),
        valuePath: 'scores.average_first_score',
      },
      { ...defaultColumn, value: intl.t(`${translationPrefix}.bestScore`), valuePath: 'scores.average_best_score' },
    ]
  }

  get exportColumns(): Column[] {
    const { intl, defaultColumn, columns } = this
    const [firstName, lastName, ...rest] = columns

    return [
      firstName,
      lastName,
      {
        ...defaultColumn,
        value: intl.t('reporting.dashboardTables.studentExternalId'),
        valuePath: 'external_id',
        comparerKey: 'sortByValue',
      },
      ...rest,
    ]
  }

  get studentRows() {
    const { router, args } = this
    return this.transformedItems.map((student) => {
      const nameCell = {
        component: ClickableCell,
        componentClassNames: 'cursor-pointer text-oceany-blue-325 font-bold print:wrap-break-word',
        onClick() {
          void router.transitionTo(args.studentRouteName, student.student_id)
        },
      }

      return {
        ...student,
        classNames: 'table-row-striped group',
        cells: [{ ...nameCell }, { ...nameCell }, {}, {}, {}, {}, {}, {}],
      }
    })
  }

  get footerRows() {
    const { durationConfig, intl } = this
    if (!this.args.aggregate) return []

    // For the known empty cells in the footer, we want no text displayed
    const emptyCell = { formatEmptyConfig: { replacement: '' } }

    const averageRow: RowObject = {
      cells: [
        { value: intl.t('tables.fluencySprints.studentResultsOverview.footers.average') },
        emptyCell,
        {
          component: DurationUnitCell,
          durationConfig,
          value: this.args.aggregate.time_on_task?.average,
        },
        emptyCell,
        { value: this.args.aggregate.sprints_mastered?.average },
        { value: this.args.aggregate.correct_answers?.average },
        { value: this.args.aggregate.scores?.average_first_score },
        { value: this.args.aggregate.scores?.average_best_score },
      ],
    }

    const totalsRow: RowObject = {
      cells: [
        { value: intl.t('tables.fluencySprints.studentResultsOverview.footers.totals') },
        emptyCell,
        {
          component: DurationUnitCell,
          durationConfig,
          value: this.args.aggregate.time_on_task?.total,
        },
        emptyCell,
        { value: this.args.aggregate.sprints_mastered?.total },
        { value: this.args.aggregate.correct_answers?.total },
        emptyCell,
        emptyCell,
      ],
    }

    const classNames = 'table-footer-cell border-t border-ms-green-100 print:border-black'
    averageRow.cells?.forEach((cell) => {
      cell.classNames = classNames
    })
    totalsRow.cells?.forEach((cell) => {
      cell.classNames = classNames
    })

    return [averageRow, totalsRow]
  }

  get exportFooterRows(): RowObject[] {
    const [averageRow, totalsRow] = this.footerRows
    if (!averageRow || !totalsRow) return []

    const [averageFirst, ...averageRest] = averageRow.cells ?? []
    const [totalsFirst, ...totalsRest] = totalsRow.cells ?? []

    const emptyCell = {
      formatEmptyConfig: { replacement: '' },
      classNames: 'table-footer-cell border-t border-ms-green-100 print:border-black',
    }

    return [{ cells: [averageFirst, emptyCell, ...averageRest] }, { cells: [totalsFirst, emptyCell, ...totalsRest] }]
  }

  <template>
    <div {{applyTransform this.performTransformItems @students this.pipelineDescriptors}}>
      <BasicTable
        class="relative w-full"
        @columns={{this.columns}}
        @rows={{this.studentRows}}
        @footerRows={{this.footerRows}}
        @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
        as |bt|
      >
        <bt.head as |h|>
          {{! Custom grouped header }}
          <tr data-test-grouped-header-row>
            <th class="p-3 print:pl-0" colspan="6">
              <div class="font-bold">{{t "tables.fluencySprints.studentResultsOverview.headers.mentalMinute"}}</div>
              <div class="font-normal">{{t
                  "tables.fluencySprints.studentResultsOverview.headers.numberFactFluency"
                }}</div>
            </th>
            <th class="table-box table-box-header bg-ms-green-150 text-center" colspan="2">
              {{t "tables.fluencySprints.studentResultsOverview.headers.numberFactsPerMinute"}}
            </th>
          </tr>
          {{! Standard header }}
          <h.row />
        </bt.head>

        {{! If not transforming and no students to show, always only display the empty message }}
        {{#if (and this.transformItems.isIdle (not this.studentRows))}}
          <bt.spanner class="p-3 text-center italic">
            {{t "tables.fluencySprints.emptyTableMessage"}}
          </bt.spanner>
        {{else}}
          {{! If transforming, always show a loading spinner}}
          {{! When no items, show it normally. }}
          {{! If there are items, place it on top of the existing table body (absolute) }}
          {{#if this.transformItems.isRunning}}
            <bt.spanner
              class="table-box table-box-cell border-transparent
                {{if this.studentRows 'absolute left-1/2 -translate-x-1/2 transform'}}"
            >
              <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
            </bt.spanner>
          {{/if}}
          <bt.body class={{if this.transformItems.isRunning "opacity-50"}} />
          <bt.foot />
        {{/if}}
      </BasicTable>
      {{#if @exportable}}
        <BasicTable
          class="hidden"
          exportable={{true}}
          @columns={{this.exportColumns}}
          @rows={{this.unpaginatedItems}}
          @footerRows={{this.exportFooterRows}}
          @config={{hash sortingConfig=@sortingConfig}}
          as |bt|
        >
          <bt.head />
          {{#if this.unpaginatedItems}}
            <bt.body />
            <bt.foot />
          {{/if}}
        </BasicTable>
      {{/if}}

      <BasePageSelector class="mx-2 my-4" @pageCount={{this.pageCount}} @itemCount={{this.unpaginatedItems.length}} />
    </div>
  </template>
}

export default FluencySprintsStudentResultsOverview
